import { useLocation } from 'react-router-dom'

import chainStepsJson from '../fixtures/chain-steps.json'
import copyJson from '../fixtures/copy.json'
import { formatUsd } from '../format'
import { useDemoStore, type DemoStoreV6 } from '../store'

export type ChainStepId = '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08'
export type ChainBaseStatus = 'done' | 'current' | 'ready' | 'awaiting_user' | 'pending' | 'error'
export type ChainNodeState = 'done' | 'current' | 'awaiting' | 'idle'
export type ChainVariant = 'normal' | 'loading' | 'error' | 'all-done' | 'no-data'

export interface ChainTrio { action: string; basis: string; result: string }

export interface ChainStep {
  id: ChainStepId
  index: number
  name: string
  shortName: string
  subtitle: string
  status: ChainBaseStatus
  node: ChainNodeState
  trio: ChainTrio
  terms: string[]
  route: string
  nextLabel: string
  requiresDecision: boolean
  userAction: string
  decided: boolean
}

export interface ChainView {
  steps: ChainStep[]
  focusId: ChainStepId
  focus: ChainStep
  variant: ChainVariant
  next: { label: string; to: string } | null
  allDone: boolean
  currentId: ChainStepId | null
  decisions: { total: number; remaining: number }
  anchorCampaignId: string
}

interface ChainFixtureStep extends ChainTrio {
  id: ChainStepId
  name: string
  shortName: string
  subtitle: string
  status: Exclude<ChainBaseStatus, 'error'>
  terms: string[]
  route: string
  nextLabel: string
  requiresDecision: boolean
  userAction: string
  states?: { approved?: ChainTrio; declined?: ChainTrio }
}

interface ChainFixture { steps: ChainFixtureStep[] }

interface ChainCopy {
  nextPrefix: string
  states: {
    noData: ChainTrio
    linkError: ChainTrio & { noNext: string }
    allDone: ChainTrio & { next: string }
  }
}

const fixture = chainStepsJson as unknown as ChainFixture
const chainCopy = copyJson.chain as unknown as ChainCopy

function samePath(left: string, right: string): boolean {
  const normalize = (value: string) => value.replace(/\/+$/, '') || '/'
  return normalize(left) === normalize(right)
}

function routeFocus(pathname: string, statuses: Record<ChainStepId, ChainBaseStatus>, currentId: ChainStepId | null): ChainStepId {
  if (samePath(pathname, '/create')) return statuses['01'] === 'done' ? '03' : '01'
  if (samePath(pathname, '/partnership')) return '02'
  if (samePath(pathname, '/creatives')) return '04'
  if (samePath(pathname, '/monitor') || pathname.startsWith('/monitor/')) return '05'
  if (samePath(pathname, '/analysis')) return '06'
  if (samePath(pathname, '/automation')) return '07'
  if (samePath(pathname, '/renewal')) return '08'
  return currentId ?? '08'
}

function syncApprovedAmount(trio: ChainTrio, proposal: DemoStoreV6['w5']['proposal']): ChainTrio {
  if (proposal.state !== 'approved' || proposal.fromDailyMax === null || proposal.dailyMax === null) return trio
  const proposed = Math.round(proposal.fromDailyMax * 1.2)
  if (proposed === proposal.dailyMax) return trio
  const source = formatUsd(proposed, 0)
  const target = formatUsd(proposal.dailyMax, 0)
  const replace = (value: string) => value.split(source).join(target)
  return { action: replace(trio.action), basis: replace(trio.basis), result: replace(trio.result) }
}

export function selectChain(
  state: DemoStoreV6,
  pathname: string,
  focusOverride?: ChainStepId | null,
): ChainView {
  const anchor = [...state.campaigns].reverse().find((campaign) => !campaign.seeded)
    ?? state.campaigns.find((campaign) => campaign.id === 'c-mug-us')
  const anchorCampaignId = anchor?.id ?? 'c-mug-us'
  const selected = state.selectedByCampaign[anchorCampaignId] ?? []
  const proposalState = state.w5?.proposal?.state ?? 'pending'
  const linkState = state.chainCreateLink?.state ?? 'idle'

  const statuses: Record<ChainStepId, ChainBaseStatus> = {
    '01': linkState === 'error'
      ? 'error'
      : !anchor
        ? 'current'
        : anchor.attentionReason === 'product_link_invalid' ? 'error' : 'done',
    '02': (state.w8?.partnership?.confirmedAt ?? null) ? 'done' : 'awaiting_user',
    '03': anchor ? 'done' : 'current',
    '04': selected.length > 0 ? 'done' : 'current',
    '05': !anchor ? 'pending' : anchor.status === 'completed' ? 'done' : 'current',
    '06': (state.w5?.reportSendCount ?? 0) > 0 ? 'done' : 'ready',
    '07': proposalState === 'pending' ? 'awaiting_user' : 'done',
    '08': (state.w8?.renewal?.confirmedAt ?? null) ? 'done' : 'pending',
  }
  const currentId = fixture.steps.find((step) => statuses[step.id] !== 'done')?.id ?? null
  const allDone = currentId === null
  const defaultFocus = routeFocus(pathname, statuses, currentId)
  const overrideStep = focusOverride ? fixture.steps.find((step) => step.id === focusOverride) : undefined
  const overrideRoute = overrideStep?.id === '05' ? `/monitor/${anchorCampaignId}` : overrideStep?.route
  const focusId = overrideStep && overrideRoute && samePath(overrideRoute, pathname) ? overrideStep.id : defaultFocus
  const focusIndex = fixture.steps.findIndex((step) => step.id === focusId)

  const steps = fixture.steps.map<ChainStep>((step, offset) => {
    const stateTrio = proposalState === 'approved' ? step.states?.approved : proposalState === 'rejected' ? step.states?.declined : undefined
    const decisionByStep: Partial<Record<ChainStepId, { decided: boolean; trio?: ChainTrio }>> = {
      '07': { decided: proposalState !== 'pending', trio: stateTrio ? syncApprovedAmount(stateTrio, state.w5.proposal) : undefined },
      '08': { decided: Boolean(state.w8?.renewal?.confirmedAt), trio: chainCopy.states.allDone },
    }
    const decision = decisionByStep[step.id]
    const decided = decision?.decided ?? false
    const trio = decided && decision?.trio ? decision.trio : { action: step.action, basis: step.basis, result: step.result }
    let node: ChainNodeState
    if (allDone) node = 'done'
    else if (statuses[step.id] === 'error') node = 'awaiting'
    else if (decided) node = 'done'
    else if (offset === focusIndex) node = 'current'
    else if (offset < focusIndex || statuses[step.id] === 'done') node = 'done'
    else if (statuses[step.id] === 'awaiting_user') node = 'awaiting'
    else node = 'idle'

    return {
      id: step.id,
      index: offset + 1,
      name: step.name,
      shortName: step.shortName,
      subtitle: step.subtitle,
      status: statuses[step.id],
      node,
      trio,
      terms: step.terms,
      route: step.id === '05' ? `/monitor/${anchorCampaignId}` : step.route,
      nextLabel: step.nextLabel,
      requiresDecision: step.requiresDecision,
      userAction: step.userAction,
      decided,
    }
  })

  const focus = steps[focusIndex] ?? steps[0]
  if (!focus) throw new Error('Chain fixture must contain eight steps')
  const loading = Boolean(state.chainBusy) || (samePath(pathname, '/create') && linkState === 'loading')
  const variant: ChainVariant = loading
    ? 'loading'
    : focusId === '01' && statuses['01'] === 'error'
      ? 'error'
      : allDone
        ? 'all-done'
        : focusId === '05' && anchor?.impressions === 0
          ? 'no-data'
          : 'normal'

  const trio = variant === 'error'
    ? chainCopy.states.linkError
    : variant === 'all-done'
      ? chainCopy.states.allDone
      : variant === 'no-data'
        ? chainCopy.states.noData
        : focus.trio
  const nextStep = steps[focusIndex + 1]
  const next = variant === 'error'
    ? null
    : variant === 'all-done'
      ? { label: chainCopy.states.allDone.next, to: '/create' }
      : focus.id === '08'
        ? { label: focus.nextLabel, to: '/overview' }
        : nextStep
          ? {
              label: `${chainCopy.nextPrefix}${focus.decided ? nextStep.name : focus.nextLabel}`,
              to: nextStep.route,
            }
          : null

  const renderedFocus = { ...focus, trio }
  return {
    steps: steps.map((step) => step.id === renderedFocus.id ? renderedFocus : step),
    focusId,
    focus: renderedFocus,
    variant,
    next,
    allDone,
    currentId,
    decisions: {
      total: steps.filter((step) => step.requiresDecision).length,
      remaining: steps.filter((step) => step.requiresDecision && step.status !== 'done').length,
    },
    anchorCampaignId,
  }
}

export function useChain(focusOverride?: ChainStepId | null): ChainView {
  const state = useDemoStore()
  const { pathname } = useLocation()
  return selectChain(state, pathname, focusOverride)
}
